import { useEffect, useState } from "react";
import {
  fetchTestimonials,
  createTestimonial,
  deleteTestimonial,
} from "@/api/auth";

export default function Testimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [form, setForm] = useState({
    name: "",
    title: "",
    content: "",
    rating: 5,
    avatar: "",
    order: 0,
    is_active: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [viewMode, setViewMode] = useState("grid"); // grid or list
  const [sortBy, setSortBy] = useState("order"); // order, rating, date

  // تحميل الآراء
  const loadTestimonials = async () => {
    try {
      const data = await fetchTestimonials();
      setTestimonials(data.sort((a, b) => a.order - b.order));
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    loadTestimonials();
  }, []);

  // فرز الآراء
  const sortedTestimonials = [...testimonials].sort((a, b) => {
    switch (sortBy) {
      case "rating":
        return b.rating - a.rating;
      case "order":
        return a.order - b.order;
      case "date":
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      default:
        return 0;
    }
  });

  // إضافة/تعديل رأي
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const testimonialData = {
        name: form.name.trim(),
        title: form.title.trim(),
        content: form.content.trim(),
        rating: Math.min(5, Math.max(1, Number(form.rating))),
        avatar: form.avatar.trim(),
        order: Number(form.order) || 0,
        is_active: form.is_active,
      };

      if (editingId) {
        // تعديل رأي موجود
        // await updateTestimonial(editingId, testimonialData);
      } else {
        // إضافة رأي جديد
        await createTestimonial(testimonialData);
      }

      setForm({
        name: "",
        title: "",
        content: "",
        rating: 5,
        avatar: "",
        order: 0,
        is_active: true,
      });
      setEditingId(null);
      loadTestimonials();
    } catch (e) {
      console.error(e);
      setError("فشل حفظ الرأي");
    } finally {
      setLoading(false);
    }
  };

  // حذف رأي
  const handleDelete = async (id) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا الرأي؟")) return;

    try {
      await deleteTestimonial(id);
      loadTestimonials();
    } catch (e) {
      console.error(e);
      setError("فشل حذف الرأي");
    }
  };

  // تحرير رأي
  const handleEdit = (testimonial) => {
    setForm({
      name: testimonial.name,
      title: testimonial.title || "",
      content: testimonial.content,
      rating: testimonial.rating,
      avatar: testimonial.avatar || "",
      order: testimonial.order,
      is_active: testimonial.is_active,
    });
    setEditingId(testimonial.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // إنشاء صورة افتراضية
  const getDefaultAvatar = (name) => {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=3b82f6&color=fff&bold=true&size=128`;
  };

  // توليد نجوم التقييم
  const renderStars = (rating) => {
    return Array(5)
      .fill(0)
      .map((_, i) => (
        <span key={i} className={`star ${i < rating ? "filled" : "empty"}`}>
          {i < rating ? "★" : "☆"}
        </span>
      ));
  };

  return (
    <div className="testimonials-container">
      <div className="testimonials-header">
        <div className="header-content">
          <h1 className="testimonials-title">آراء وتقييمات العملاء</h1>
          <p className="testimonials-subtitle">إدارة الشهادات والآراء التي تظهر في موقعك</p>
        </div>
        <div className="header-stats">
          <div className="stat">
            <span className="stat-value">{testimonials.length}</span>
            <span className="stat-label">إجمالي الآراء</span>
          </div>
          <div className="stat">
            <span className="stat-value">
              {testimonials.filter(t => t.is_active).length}
            </span>
            <span className="stat-label">نشطة</span>
          </div>
          <div className="stat">
            <span className="stat-value">
              {testimonials.length > 0 
                ? (testimonials.reduce((sum, t) => sum + t.rating, 0) / testimonials.length).toFixed(1)
                : "0.0"
              }
            </span>
            <span className="stat-label">متوسط التقييم</span>
          </div>
        </div>
      </div>

      {/* نموذج الإضافة/التعديل */}
      <div className="testimonials-form-container">
        <h2 className="form-title">
          {editingId ? "تعديل رأي العميل" : "إضافة رأي جديد"}
        </h2>
        
        <form onSubmit={handleSubmit} className="testimonials-form">
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">
                اسم العميل *
                <span className="label-hint">الاسم الكامل للعميل</span>
              </label>
              <input
                className="form-input"
                placeholder="مثال: أحمد محمد"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                المنصب / الصفة
                <span className="label-hint">مثال: مدير أكاديمية، مؤسس شركة</span>
              </label>
              <input
                className="form-input"
                placeholder="مثال: مدير أكاديمية البرمجة"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                نص الرأي *
                <span className="label-hint">آراء العميل حول الخدمة</span>
              </label>
              <textarea
                className="form-textarea"
                placeholder="اكتب نص الرأي هنا..."
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                required
                rows={4}
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                التقييم
                <span className="label-hint">من 1 إلى 5 نجوم</span>
              </label>
              <div className="rating-input">
                <div className="stars-preview">
                  {renderStars(form.rating)}
                </div>
                <input
                  className="rating-slider"
                  type="range"
                  min="1"
                  max="5"
                  step="1"
                  value={form.rating}
                  onChange={(e) => setForm({ ...form, rating: Number(e.target.value) })}
                />
                <span className="rating-value">{form.rating} / 5</span>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                رابط الصورة (Avatar)
                <span className="label-hint">رابط الصورة الشخصية (اختياري)</span>
              </label>
              <input
                className="form-input"
                placeholder="https://example.com/avatar.jpg"
                value={form.avatar}
                onChange={(e) => setForm({ ...form, avatar: e.target.value })}
              />
              {form.avatar && (
                <div className="avatar-preview">
                  <img 
                    src={form.avatar} 
                    alt="معاينة الصورة"
                    onError={(e) => {
                      e.target.src = getDefaultAvatar(form.name);
                    }}
                  />
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                الترتيب
                <span className="label-hint">رقم يحدد ترتيب الظهور</span>
              </label>
              <input
                className="form-input"
                type="number"
                placeholder="0"
                value={form.order}
                onChange={(e) => setForm({ ...form, order: e.target.value })}
                min="0"
              />
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  className="checkbox-input"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
                <span className="checkbox-text">تفعيل الرأي</span>
                <span className="checkbox-hint">عرض هذا الرأي في الموقع</span>
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button 
              type="submit" 
              className="submit-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  جاري الحفظ...
                </>
              ) : editingId ? "حفظ التعديلات" : "إضافة الرأي"}
            </button>
            
            {editingId && (
              <button 
                type="button"
                className="cancel-btn"
                onClick={() => {
                  setForm({
                    name: "",
                    title: "",
                    content: "",
                    rating: 5,
                    avatar: "",
                    order: 0,
                    is_active: true,
                  });
                  setEditingId(null);
                }}
              >
                إلغاء التعديل
              </button>
            )}
          </div>

          {error && (
            <div className="error-alert">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}
        </form>
      </div>

      {/* أدوات التحكم */}
      <div className="controls-container">
        <div className="view-controls">
          <button 
            className={`view-btn ${viewMode === "grid" ? "active" : ""}`}
            onClick={() => setViewMode("grid")}
          >
            <span className="view-icon">⏹️</span>
            عرض شبكي
          </button>
          <button 
            className={`view-btn ${viewMode === "list" ? "active" : ""}`}
            onClick={() => setViewMode("list")}
          >
            <span className="view-icon">📋</span>
            عرض قائمة
          </button>
        </div>

        <div className="sort-controls">
          <label className="sort-label">ترتيب حسب:</label>
          <select 
            className="sort-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="order">الترتيب</option>
            <option value="rating">التقييم</option>
            <option value="date">الأحدث</option>
          </select>
        </div>
      </div>

      {/* قائمة الآراء */}
      <div className="testimonials-list-container">
        {sortedTestimonials.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">💬</div>
            <h3 className="empty-title">لا توجد آراء مضافة بعد</h3>
            <p className="empty-subtitle">ابدأ بإضافة أول رأي للعملاء</p>
          </div>
        ) : (
          <div className={`testimonials-${viewMode}`}>
            {sortedTestimonials.map((testimonial) => (
              <div 
                key={testimonial.id} 
                className={`testimonial-card ${!testimonial.is_active ? "inactive" : ""} ${editingId === testimonial.id ? "editing" : ""}`}
              >
                <div className="card-header">
                  <div className="avatar-container">
                    <img
                      src={testimonial.avatar || getDefaultAvatar(testimonial.name)}
                      alt={testimonial.name}
                      className="testimonial-avatar"
                      onError={(e) => {
                        e.target.src = getDefaultAvatar(testimonial.name);
                      }}
                    />
                  </div>
                  
                  <div className="user-info">
                    <h3 className="user-name">{testimonial.name}</h3>
                    {testimonial.title && (
                      <p className="user-title">{testimonial.title}</p>
                    )}
                    <div className="user-rating">
                      {renderStars(testimonial.rating)}
                      <span className="rating-number">{testimonial.rating}.0</span>
                    </div>
                  </div>
                </div>

                <div className="card-content">
                  <p className="testimonial-content">{testimonial.content}</p>
                </div>

                <div className="card-footer">
                  <div className="footer-meta">
                    <span className="meta-item">
                      <span className="meta-icon">#</span>
                      الترتيب: {testimonial.order}
                    </span>
                    <span className="meta-item">
                      <span className="meta-icon">📅</span>
                      {testimonial.created_at 
                        ? new Date(testimonial.created_at).toLocaleDateString('ar-SA')
                        : "غير محدد"
                      }
                    </span>
                    <span className={`status-badge ${testimonial.is_active ? "active" : "inactive"}`}>
                      {testimonial.is_active ? "مفعل" : "مخفي"}
                    </span>
                  </div>

                  <div className="footer-actions">
                    <button 
                      className="action-btn edit-btn"
                      onClick={() => handleEdit(testimonial)}
                    >
                      <span className="action-icon">✏️</span>
                      تعديل
                    </button>
                    <button 
                      className="action-btn delete-btn"
                      onClick={() => handleDelete(testimonial.id)}
                    >
                      <span className="action-icon">🗑️</span>
                      حذف
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <style jsx>{`
        .testimonials-container {
          padding: 2rem;
          max-width: 1200px;
          margin: 0 auto;
        }

        .testimonials-header {
          background: linear-gradient(135deg, #8b5cf6, #6366f1);
          border-radius: 16px;
          padding: 2rem;
          color: white;
          margin-bottom: 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 2rem;
        }

        .header-content {
          flex: 1;
        }

        .testimonials-title {
          font-size: 2.2rem;
          margin-bottom: 0.5rem;
          font-weight: 700;
        }

        .testimonials-subtitle {
          font-size: 1.1rem;
          opacity: 0.9;
          margin: 0;
        }

        .header-stats {
          display: flex;
          gap: 1.5rem;
        }

        .stat {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 1rem;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          min-width: 100px;
        }

        .stat-value {
          font-size: 2rem;
          font-weight: 700;
          margin-bottom: 0.25rem;
        }

        .stat-label {
          font-size: 0.875rem;
          opacity: 0.8;
        }

        .testimonials-form-container {
          background: white;
          border-radius: 16px;
          padding: 2rem;
          margin-bottom: 2rem;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          border: 1px solid #e2e8f0;
        }

        .form-title {
          color: #1e293b;
          font-size: 1.5rem;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 2px solid #8b5cf6;
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .form-group:nth-child(3),
        .form-group:nth-child(7),
        .form-group:nth-child(8) {
          grid-column: 1 / -1;
        }

        .form-label {
          color: #334155;
          font-weight: 600;
          margin-bottom: 0.5rem;
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .label-hint {
          color: #64748b;
          font-weight: normal;
          font-size: 0.875rem;
        }

        .form-input {
          padding: 0.875rem 1rem;
          border: 2px solid #cbd5e1;
          border-radius: 10px;
          font-size: 1rem;
          transition: all 0.3s ease;
          background: #f8fafc;
          width: 100%;
        }

        .form-input:focus {
          outline: none;
          border-color: #8b5cf6;
          background: white;
          box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
        }

        .form-textarea {
          padding: 1rem;
          border: 2px solid #cbd5e1;
          border-radius: 10px;
          font-size: 1rem;
          transition: all 0.3s ease;
          background: #f8fafc;
          resize: vertical;
          font-family: inherit;
          width: 100%;
        }

        .form-textarea:focus {
          outline: none;
          border-color: #8b5cf6;
          background: white;
          box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
        }

        .rating-input {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .stars-preview {
          display: flex;
          gap: 0.25rem;
          font-size: 1.5rem;
        }

        .star {
          color: #cbd5e1;
          transition: color 0.2s ease;
        }

        .star.filled {
          color: #f59e0b;
        }

        .rating-slider {
          width: 100%;
          height: 8px;
          -webkit-appearance: none;
          background: linear-gradient(to right, #f59e0b 0%, #f59e0b 100%);
          border-radius: 4px;
          outline: none;
        }

        .rating-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 20px;
          height: 20px;
          background: #f59e0b;
          border-radius: 50%;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 5px rgba(0,0,0,0.2);
        }

        .rating-value {
          text-align: center;
          font-weight: 600;
          color: #f59e0b;
        }

        .avatar-preview {
          margin-top: 1rem;
          display: flex;
          justify-content: center;
        }

        .avatar-preview img {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          object-fit: cover;
          border: 3px solid #e2e8f0;
        }

        .checkbox-group {
          padding: 1rem;
          background: #f1f5f9;
          border-radius: 10px;
        }

        .checkbox-label {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          cursor: pointer;
        }

        .checkbox-input {
          width: 18px;
          height: 18px;
          accent-color: #8b5cf6;
          margin-left: 0.5rem;
        }

        .checkbox-text {
          font-weight: 600;
          color: #334155;
        }

        .checkbox-hint {
          color: #64748b;
          font-size: 0.875rem;
        }

        .form-actions {
          display: flex;
          gap: 1rem;
          align-items: center;
        }

        .submit-btn {
          background: linear-gradient(135deg, #8b5cf6, #6366f1);
          color: white;
          border: none;
          padding: 0.875rem 2rem;
          border-radius: 10px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          min-width: 160px;
        }

        .submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(139, 92, 246, 0.3);
        }

        .submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .cancel-btn {
          background: #f1f5f9;
          color: #64748b;
          border: 2px solid #cbd5e1;
          padding: 0.875rem 2rem;
          border-radius: 10px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .cancel-btn:hover {
          background: #e2e8f0;
          color: #475569;
        }

        .spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .error-alert {
          background: #fef2f2;
          color: #dc2626;
          padding: 1rem;
          border-radius: 10px;
          border: 1px solid #fecaca;
          margin-top: 1.5rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .error-icon {
          font-size: 1.2rem;
        }

        .controls-container {
          background: white;
          border-radius: 12px;
          padding: 1.5rem;
          margin-bottom: 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
          border: 1px solid #e2e8f0;
        }

        .view-controls {
          display: flex;
          gap: 0.5rem;
        }

        .view-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: #f1f5f9;
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          color: #64748b;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .view-btn:hover {
          background: #e2e8f0;
          color: #475569;
        }

        .view-btn.active {
          background: #8b5cf6;
          color: white;
          border-color: #8b5cf6;
        }

        .view-icon {
          font-size: 1rem;
        }

        .sort-controls {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .sort-label {
          color: #334155;
          font-weight: 600;
          white-space: nowrap;
        }

        .sort-select {
          padding: 0.75rem 1rem;
          border: 2px solid #cbd5e1;
          border-radius: 8px;
          background: white;
          color: #334155;
          font-weight: 500;
          min-width: 150px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .sort-select:focus {
          outline: none;
          border-color: #8b5cf6;
          box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
        }

        .testimonials-list-container {
          background: white;
          border-radius: 16px;
          padding: 2rem;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          border: 1px solid #e2e8f0;
        }

        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
        }

        .empty-icon {
          font-size: 4rem;
          margin-bottom: 1rem;
          opacity: 0.3;
        }

        .empty-title {
          color: #475569;
          margin-bottom: 0.5rem;
        }

        .empty-subtitle {
          color: #94a3b8;
        }

        .testimonials-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 1.5rem;
        }

        .testimonials-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .testimonial-card {
          background: white;
          border: 2px solid #e2e8f0;
          border-radius: 16px;
          overflow: hidden;
          transition: all 0.3s ease;
        }

        .testimonials-grid .testimonial-card {
          height: 100%;
        }

        .testimonials-list .testimonial-card {
          flex-direction: row;
        }

        .testimonial-card:hover {
          border-color: #cbd5e1;
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
        }

        .testimonial-card.inactive {
          opacity: 0.6;
          background: #f8fafc;
        }

        .testimonial-card.editing {
          border-color: #3b82f6;
          background: #eff6ff;
        }

        .card-header {
          display: flex;
          align-items: center;
          padding: 1.5rem;
          background: linear-gradient(135deg, #f8fafc, #e2e8f0);
          gap: 1rem;
        }

        .avatar-container {
          flex-shrink: 0;
        }

        .testimonial-avatar {
          width: 70px;
          height: 70px;
          border-radius: 50%;
          object-fit: cover;
          border: 3px solid white;
          box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
        }

        .user-info {
          flex: 1;
        }

        .user-name {
          color: #1e293b;
          font-size: 1.25rem;
          font-weight: 700;
          margin: 0 0 0.25rem 0;
        }

        .user-title {
          color: #64748b;
          margin: 0 0 0.5rem 0;
          font-size: 0.875rem;
        }

        .user-rating {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .rating-number {
          color: #f59e0b;
          font-weight: 700;
          font-size: 1rem;
        }

        .card-content {
          padding: 1.5rem;
          border-bottom: 1px solid #e2e8f0;
        }

        .testimonial-content {
          color: #475569;
          line-height: 1.8;
          margin: 0;
          font-style: italic;
          quotes: "\\201C" "\\201D";
        }

        .testimonial-content::before {
          content: open-quote;
          font-size: 2rem;
          color: #cbd5e1;
          line-height: 0;
          margin-right: 0.5rem;
        }

        .card-footer {
          padding: 1.25rem 1.5rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .footer-meta {
          display: flex;
          align-items: center;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .meta-item {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          color: #64748b;
          font-size: 0.875rem;
        }

        .meta-icon {
          font-size: 1rem;
        }

        .status-badge {
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .status-badge.active {
          background: #d1fae5;
          color: #059669;
        }

        .status-badge.inactive {
          background: #fecaca;
          color: #dc2626;
        }

        .footer-actions {
          display: flex;
          gap: 0.75rem;
        }

        .action-btn {
          padding: 0.5rem 1.25rem;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          border: none;
        }

        .edit-btn {
          background: #dbeafe;
          color: #1d4ed8;
        }

        .edit-btn:hover {
          background: #bfdbfe;
        }

        .delete-btn {
          background: #fee2e2;
          color: #dc2626;
        }

        .delete-btn:hover {
          background: #fecaca;
        }

        .action-icon {
          font-size: 1rem;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .testimonials-container {
            padding: 1rem;
          }

          .testimonials-header {
            flex-direction: column;
            text-align: center;
            padding: 1.5rem;
          }

          .header-stats {
            width: 100%;
            justify-content: center;
          }

          .form-grid {
            grid-template-columns: 1fr;
          }

          .controls-container {
            flex-direction: column;
            align-items: stretch;
          }

          .view-controls {
            justify-content: center;
          }

          .sort-controls {
            justify-content: center;
          }

          .testimonials-grid {
            grid-template-columns: 1fr;
          }

          .testimonials-list .testimonial-card {
            flex-direction: column;
          }

          .card-footer {
            flex-direction: column;
            align-items: stretch;
          }

          .footer-meta {
            justify-content: center;
          }

          .footer-actions {
            justify-content: center;
          }

          .form-actions {
            flex-direction: column;
            align-items: stretch;
          }

          .submit-btn, .cancel-btn {
            width: 100%;
            justify-content: center;
          }
        }

        @media (max-width: 480px) {
          .testimonials-title {
            font-size: 1.75rem;
          }

          .testimonials-form-container,
          .testimonials-list-container {
            padding: 1.5rem;
          }

          .stat {
            min-width: 80px;
            padding: 0.75rem;
          }

          .stat-value {
            font-size: 1.5rem;
          }

          .card-header {
            flex-direction: column;
            text-align: center;
          }

          .user-info {
            text-align: center;
          }

          .user-rating {
            justify-content: center;
          }

          .footer-actions {
            flex-direction: column;
          }

          .action-btn {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
}